import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { 
  User, 
  LogOut, 
  Settings, 
  Video, 
  Crown,
  Zap,
  BarChart3,
  ArrowRight
} from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'enterprise': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'pro': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(16, 185, 129, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(16, 185, 129, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-slate-800 bg-slate-950/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/vritta-logo.png" alt="Vrittaa AI" className="w-8 h-8" />
            <span className="text-xl font-bold font-outfit text-white tracking-wider">VRITTAA</span>
          </Link>

          <div className="flex items-center gap-4">
            {isAdmin && (
              <Link to="/admin/showcase">
                <Button variant="outline" size="sm" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                  <Settings className="w-4 h-4 mr-2" />
                  Admin
                </Button>
              </Link>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-slate-400 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white font-outfit mb-2">
                Welcome, {user?.name || 'User'}!
              </h1>
              <p className="text-slate-400">Manage your automations and explore Vrittaa AI features.</p>
            </div>
            <div className={`px-4 py-2 rounded-full border ${getPlanBadgeColor(user?.plan)}`}>
              <div className="flex items-center gap-2">
                <Crown className="w-4 h-4" />
                <span className="font-medium capitalize">{user?.plan || 'Free'} Plan</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 mb-8"
        >
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <User className="w-8 h-8 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">{user?.name}</h2>
              <p className="text-slate-400">{user?.email}</p>
              <p className="text-xs text-slate-500 mt-1">
                Signed in via {user?.provider === 'google' ? 'Google' : user?.provider === 'both' ? 'Google & Email' : 'Email'}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
        >
          {/* Showcase */}
          <Link to="/showcase" className="group">
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 hover:border-emerald-500/30 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-4 group-hover:bg-emerald-500/30 transition-colors">
                <Video className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Product Showcase</h3>
              <p className="text-slate-400 text-sm mb-4">Watch demo videos and explore features</p>
              <span className="text-emerald-400 text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                View Showcase <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>

          {/* Pricing */}
          <Link to="/pricing" className="group">
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 hover:border-cyan-500/30 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center mb-4 group-hover:bg-cyan-500/30 transition-colors">
                <Zap className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Upgrade Plan</h3>
              <p className="text-slate-400 text-sm mb-4">Unlock more features with Pro or Enterprise</p>
              <span className="text-cyan-400 text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                View Plans <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>

          {/* Analytics (Coming Soon) */}
          <div className="group opacity-60">
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mb-4">
                <BarChart3 className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Analytics</h3>
              <p className="text-slate-400 text-sm mb-4">Track your automation performance</p>
              <span className="text-slate-500 text-sm">Coming Soon</span>
            </div>
          </div>
        </motion.div>

        {/* Admin Section */}
        {isAdmin && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 rounded-2xl p-6"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-5 h-5 text-emerald-400" />
                  <h3 className="text-lg font-semibold text-white">Admin Access</h3>
                </div>
                <p className="text-slate-400 text-sm">You have admin privileges. Manage showcase videos and platform settings.</p>
              </div>
              <Link to="/admin/showcase">
                <Button className="bg-emerald-600 hover:bg-emerald-500 text-white">
                  <Settings className="w-4 h-4 mr-2" />
                  Manage Showcase
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
